import { useEffect, useMemo, useState } from 'react';

export type MaintenanceStatus = {
  enabled: boolean;
  message?: string;
  source?: 'env' | 'remote';
};

const parseEnvBool = (value?: string) => {
  const v = (value || '').trim().toLowerCase();
  return v === '1' || v === 'true' || v === 'yes' || v === 'on';
};

const getEnvDefault = (): MaintenanceStatus => {
  const enabled = parseEnvBool(process.env.REACT_APP_MAINTENANCE_MODE);
  const message = (process.env.REACT_APP_MAINTENANCE_MESSAGE || '').trim() || undefined;
  return { enabled, message, source: 'env' };
};

export const useMaintenanceStatus = () => {
  const envDefault = useMemo(() => getEnvDefault(), []);
  const [status, setStatus] = useState<MaintenanceStatus>(envDefault);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 2500);

    setLoading(true);
    fetch(`/maintenance.json?_=${Date.now()}`, {
      signal: controller.signal,
      cache: 'no-store',
    })
      .then(async (res) => {
        if (!res.ok) return null;
        try {
          return (await res.json()) as any;
        } catch {
          return null;
        }
      })
      .then((data) => {
        if (cancelled || !data) return;

        const remoteEnabled = Boolean(data.enabled ?? data.maintenance ?? false);
        const messageRaw = data.message ?? data.text ?? data.description;
        const message = typeof messageRaw === 'string' ? messageRaw.trim() : undefined;

        setStatus((prev) => ({
          // env (если включил maintenance) имеет приоритет над remote-флагом
          enabled: prev.enabled || remoteEnabled,
          message: message || prev.message,
          source: 'remote',
        }));
      })
      .catch(() => {})
      .finally(() => {
        window.clearTimeout(timeoutId);
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, []);

  return { status, loading };
};
